import { useParams, useSearchParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { useMutation } from 'urql'
import { OpenHours, ProviderType } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser } from 'app/utils'
import { useProviderStore, useAppointmentTypeStore, useLocationStore } from 'app/stores/StoreContext'
import { gqlQuery } from 'app/lib/graphqlClient'
import { OPS_URL, opsClient } from 'app/lib/opsUrqlClient'
import { graphql } from 'app/generated/gql'
import { logError } from 'lib/logging'

const OPERATORIES_QUERY = graphql(`
  query AvailabilityViewOperatories($locationId: ID!) {
    operatories(locationId: $locationId) {
      name
      remote_id
      type
      display_name
      id
      locationId
    }
  }
`)

const SYNC_PROVIDERS_MUTATION = graphql(`
  mutation SyncProviders($locationId: ID!) {
    syncProviders(locationId: $locationId) {
      success
      error
    }
  }
`)

const SYNC_SCHEDULES_MUTATION = graphql(`
  mutation SyncSchedules($locationId: ID!) {
    syncSchedules(locationId: $locationId) {
      success
      error
    }
  }
`)

export type UseAvailabilityViewReturn = {
  user: ReturnType<typeof useUser>
  providers: any
  bookableAppointmentTypes: any
  operatories: any
  operatoriesError: any
  operatoriesSlow: boolean | undefined
  isSyncing: boolean
  isSyncingSchedules: boolean
  rows: ProviderType[]
  view: `all` | `active` | `inactive`
  activeProviders: ProviderType[]
  inactiveProviders: ProviderType[]
  isDentrix: boolean
  onRefreshClick: () => void
  onSyncSchedulesClick: () => void
  onViewChange: (view: `all` | `active` | `inactive`) => void
  handleProviderStatusChange: (provider: ProviderType, active: boolean) => void
  locationOpenHours: OpenHours | undefined
  /** Undefined until the location loads, so the page does not flash the Advanced layout. */
  isStandardMode: boolean | undefined
}

export function useAvailabilityView(): UseAvailabilityViewReturn {
  const user = useUser()
  const { locationId } = useParams()
  const providerStore = useProviderStore()
  const appointmentTypeStore = useAppointmentTypeStore()
  const locationStore = useLocationStore()
  const [, executeSyncProviders] = useMutation(SYNC_PROVIDERS_MUTATION)
  const [, executeSyncSchedules] = useMutation(SYNC_SCHEDULES_MUTATION)
  const [providers, setProviders] = useState<any>()
  const [bookableAppointmentTypes, setBookableAppointmentTypes] = useState<any>()
  const [operatories, setOperatories] = useState<any>()
  const [operatoriesError, setOperatoriesError] = useState<any>()
  const [operatoriesSlow, setOperatoriesSlow] = useState<boolean | undefined>()
  const [isDentrix, setIsDentrix] = useState(false)
  const [locationOpenHours, setLocationOpenHours] = useState<OpenHours | undefined>()
  const [isStandardMode, setIsStandardMode] = useState<boolean | undefined>()
  const [isSyncing, setIsSyncing] = useState(false)
  const [isSyncingSchedules, setIsSyncingSchedules] = useState(false)
  const [rows, setRows] = useState<ProviderType[]>([])
  const [searchParams, setSearchParams] = useSearchParams()
  const [view, setView] = useState<`all` | `active` | `inactive`>(
    (searchParams.get(`view`) as `all` | `active` | `inactive`) || `active`,
  )

  useEffect(() => {
    if (!locationId) return

    // Fetch providers via GraphQL
    providerStore
      .fetchList()
      .then((result) => {
        if (result.data) {
          const list = (result.data as any).providers
          setProviders(list)
          if (list?.hits) setRows(list.hits as ProviderType[])
        }
      })
      .catch((err) => logError(`Error fetching providers`, err))

    // Fetch appointment types via GraphQL
    appointmentTypeStore
      .fetchList({ locationId } as any)
      .then((result) => {
        if (result.data) setBookableAppointmentTypes((result.data as any).appointmentTypes)
      })
      .catch((err) => logError(`Error fetching appointment types`, err))

    // Fetch location for isDentrix and the availability mode
    locationStore
      .fetchOne(locationId)
      .then((result) => {
        const loc = (result.data as any)?.location ?? result.data
        if (loc) {
          setIsDentrix(loc.pms === `Dentrix` || loc.pms === `dentrix`)
          setLocationOpenHours(loc.openHours ?? undefined)
        }
        // Same test the API uses: Standard mode only applies to Kolla-connected locations.
        setIsStandardMode(loc?.availabilityMode === `standard` && !!loc?.kollaConsumerId)
      })
      .catch((err) => {
        setIsStandardMode(false)
        logError(`Error fetching location`, err)
      })

    // Fetch operatories via GraphQL
    const slowTimer = setTimeout(() => setOperatoriesSlow(true), 8000)
    gqlQuery(OPERATORIES_QUERY, { locationId }, { client: opsClient })
      .then((result) => {
        clearTimeout(slowTimer)
        setOperatoriesSlow(false)
        if (result.data) setOperatories(result.data.operatories)
      })
      .catch((err) => {
        clearTimeout(slowTimer)
        setOperatoriesSlow(false)
        setOperatoriesError(err)
        logError(`Error fetching operatories`, err)
      })
  }, [locationId])

  const activeProviders = useMemo(() => rows?.filter((provider) => provider.deactivated !== true), [rows])
  const inactiveProviders = rows?.filter((provider) => provider.deactivated === true)

  const onRefreshClick = () => {
    setIsSyncing(true)
    executeSyncProviders({ locationId: locationId! }, { url: OPS_URL })
      .then(async (result) => {
        if (result.data?.syncProviders?.success) {
          const fetchResult = await providerStore.fetchList()
          if (fetchResult.data) {
            const newHits = ((fetchResult.data as any).providers?.hits ?? []) as ProviderType[]
            const existingIds = new Set(rows.map((p) => p.id))
            const actuallyNew = newHits.filter((p) => !existingIds.has(p.id))
            setRows(newHits)
            notifySuccess(actuallyNew.length ? `${actuallyNew.length} New Providers Added` : `No New Providers Added`)
          }
          onViewChange(`all`)
        } else {
          notifyError(result.data?.syncProviders?.error || `Error syncing providers`)
        }
        setIsSyncing(false)
      })
      .catch(() => {
        setIsSyncing(false)
        notifyError(`Error syncing providers`)
      })
  }

  const onSyncSchedulesClick = () => {
    setIsSyncingSchedules(true)
    executeSyncSchedules({ locationId: locationId! }, { url: OPS_URL })
      .then((result) => {
        if (result.data?.syncSchedules?.success) {
          notifySuccess(`PMS Schedules Synced`)
        } else {
          notifyError(result.data?.syncSchedules?.error || `Error syncing schedules`)
        }
        setIsSyncingSchedules(false)
      })
      .catch(() => {
        setIsSyncingSchedules(false)
        notifyError(`Error syncing schedules`)
      })
  }

  const onViewChange = (v: `all` | `active` | `inactive`) => {
    setView(v)
    setSearchParams({ view: v })
  }

  const handleProviderStatusChange = (provider: ProviderType, active: boolean) => {
    setRows((prev) => prev.map((p) => (p.id === provider.id ? { ...p, deactivated: !active } : p)))
  }

  return {
    user,
    providers,
    bookableAppointmentTypes,
    operatories,
    operatoriesError,
    operatoriesSlow,
    isSyncing,
    isSyncingSchedules,
    rows,
    view,
    activeProviders,
    inactiveProviders,
    isDentrix,
    onRefreshClick,
    onSyncSchedulesClick,
    onViewChange,
    handleProviderStatusChange,
    locationOpenHours,
    isStandardMode,
  }
}
